import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';
import { createScenarioFromSources } from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';
import { bangkokLocalInputToIso } from './utils/bangkokTime';

// ASSESSMENT.md P2: "createScenario ตรวจเพียง start/end ไม่ว่าง" — end<=start or same-day
// windows fell through to a generic backend error. We validate end > start locally and, per
// IMPLEMENTATION_PLAN.md Phase 2 ("temporary same-day restriction จน backend date filter แก้"),
// block same-day windows until the backend's SAP date filtering is confirmed to handle them.
export default function ScenarioCreate({ plant, factory, onCreated }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const [planningStart, setPlanningStart] = useState('');
  const [planningEnd, setPlanningEnd] = useState('');
  const [useExistingStock, setUseExistingStock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const validate = () => {
    if (!planningStart || !planningEnd) {
      return 'Select both planning start and planning end.';
    }
    const start = new Date(planningStart);
    const end = new Date(planningEnd);
    if (end.getTime() <= start.getTime()) {
      return 'Planning end must be after planning start.';
    }
    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      return 'Select an end date after the start date. Demand is selected by SAP production date; the end date is excluded.';
    }
    return null;
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setMessage({ severity: 'error', text: validationError });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const response = await createScenarioFromSources({
        plant,
        factory,
        planningStart: bangkokLocalInputToIso(planningStart),
        planningEnd: bangkokLocalInputToIso(planningEnd),
        useExistingStock,
        accessToken,
      });
      setMessage({ severity: 'success', text: `Draft scenario #${response.data.id} created from SAP demand, product master, and in-stock tumbler records.` });
      onCreated?.(response.data);
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to create scenario from source data.').summary });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Planning start (Bangkok)"
          type="datetime-local"
          value={planningStart}
          onChange={(event) => setPlanningStart(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Planning end (Bangkok)"
          type="datetime-local"
          value={planningEnd}
          onChange={(event) => setPlanningEnd(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControlLabel
          control={<Checkbox checked={useExistingStock} onChange={(event) => setUseExistingStock(event.target.checked)} />}
          label="Use existing in-stock tumbler records"
        />
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create from sources'}
        </Button>
      </Stack>
    </Stack>
  );
}
